import React from 'react';
import styled from 'styled-components';
import Icon from './Icon';
import Button from './Button';

/**
 * What a campaign sends, and how long it waits between messages.
 *
 * This shows the *shape* of a sequence, not its content. Follow-ups are written
 * when they fall due — against the thread so far, so they can refer back rather
 * than restate the pitch — which means there is nothing to preview here. A body
 * field would promise an editor that does not exist, and a preview would be of
 * words that will not be the words sent.
 *
 * Three steps is the ceiling: the states behind this are positional, and going
 * further would mean replacing them with a step index that the reply matcher,
 * the approvals queue and the Leads status column all read.
 */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.65rem 0.8rem;
  background: var(--surface-2);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
`;

const Ordinal = styled.span`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--accent-cyan-dim);
  color: var(--accent-cyan);
  font-size: var(--text-xs);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const What = styled.div`
  flex: 1;
  min-width: 0;
  color: var(--text-primary);
  font-size: var(--text-sm);
`;

const When = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  white-space: nowrap;
`;

const Days = styled.input`
  width: 3.2rem;
  background: var(--surface-3);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 0.28rem 0.4rem;
  color: var(--text-primary);
  font-family: inherit;
  font-size: var(--text-sm);
  text-align: center;
  font-variant-numeric: tabular-nums;

  &:focus { outline: none; border-color: var(--border-accent); }
`;

const Note = styled.p`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 1.55;
  margin: 0.15rem 0 0;
`;

const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.6rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid ${(p) => (p.$on ? 'var(--border-accent)' : 'var(--border-color)')};
  background: ${(p) => (p.$on ? 'var(--accent-cyan-dim)' : 'transparent')};
  cursor: pointer;

  input { width: auto; margin: 0.15rem 0 0; accent-color: var(--accent-cyan); }
`;

const ChoiceText = styled.div`
  color: var(--text-primary);
  font-size: var(--text-sm);

  small {
    display: block;
    color: var(--text-muted);
    font-size: var(--text-xs);
    line-height: 1.5;
    margin-top: 0.15rem;
  }
`;

const MAX_STEPS = 3;

/** What each position in a sequence is for, in a person's words. */
const ROLE = [
  { key: 'opening', label: 'Opening email', hint: 'Researched, and the one you approve.' },
  { key: 'follow_up_1', label: 'Follow-up', hint: 'A short nudge. Written when it falls due.' },
  { key: 'follow_up_2', label: 'Final note', hint: 'Gracious, and makes it easy to say no.' }
];

export const DEFAULT_STEPS = [
  { key: 'opening', waitDays: 0 },
  { key: 'follow_up_1', waitDays: 3 },
  { key: 'follow_up_2', waitDays: 5 }
];

function SequenceEditor({ steps, onChange, autonomyMode, onAutonomyChange }) {
  const list = (steps && steps.length ? steps : DEFAULT_STEPS).slice(0, MAX_STEPS);

  const setDays = (i, value) => {
    const days = Math.max(1, Math.min(60, parseInt(value, 10) || 1));
    onChange(list.map((s, n) => (n === i ? { ...s, waitDays: days } : s)));
  };

  return (
    <Wrap>
      {list.map((step, i) => (
        <Step key={step.key || i}>
          <Ordinal>{i + 1}</Ordinal>
          <What>
            {(ROLE[i] || {}).label || step.key}
            <Note>{(ROLE[i] || {}).hint}</Note>
          </What>
          {i === 0 ? (
            <When>sent first</When>
          ) : (
            <When>
              <Days
                type="number"
                min="1"
                max="60"
                value={step.waitDays ?? 3}
                onChange={(e) => setDays(i, e.target.value)}
                aria-label={`Days to wait before ${(ROLE[i] || {}).label || step.key}`}
              />
              days later
            </When>
          )}
          {list.length > 1 && i === list.length - 1 && (
            <Button
              $variant="ghost"
              $size="sm"
              type="button"
              title="Remove this step"
              onClick={() => onChange(list.slice(0, -1))}
            >
              <Icon name="close" size={13} />
            </Button>
          )}
        </Step>
      ))}

      {list.length < MAX_STEPS && (
        <Button
          $variant="secondary"
          type="button"
          onClick={() => onChange([...list, { key: ROLE[list.length].key, waitDays: 4 }])}
        >
          <Icon name="plus" size={14} /> Add {(ROLE[list.length] || {}).label || 'a step'}
        </Button>
      )}

      <Note>
        Anyone who replies drops out of the sequence immediately, and nobody is
        written to more than {list.length} time{list.length === 1 ? '' : 's'}.
      </Note>

      <div style={{ marginTop: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <Choice $on={autonomyMode === 'manual'}>
          <input
            type="radio"
            name="autonomy"
            checked={autonomyMode === 'manual'}
            onChange={() => onAutonomyChange('manual')}
          />
          <ChoiceText>
            Show me every email before it goes
            <small>
              Each follow-up waits in Approvals too, written against the thread
              and ready to edit. The slowest option, and the most careful.
            </small>
          </ChoiceText>
        </Choice>

        <Choice $on={autonomyMode !== 'autopilot' && autonomyMode !== 'manual'}>
          <input
            type="radio"
            name="autonomy"
            checked={autonomyMode !== 'autopilot' && autonomyMode !== 'manual'}
            onChange={() => onAutonomyChange('batch_approve')}
          />
          <ChoiceText>
            Show me the first email before it goes
            <small>
              It waits in Approvals. Approving it also approves the follow-ups,
              which then send on the schedule above.
            </small>
          </ChoiceText>
        </Choice>

        <Choice $on={autonomyMode === 'autopilot'}>
          <input
            type="radio"
            name="autonomy"
            checked={autonomyMode === 'autopilot'}
            onChange={() => onAutonomyChange('autopilot')}
          />
          <ChoiceText>
            Send without asking me
            <small>Nothing waits in Approvals. Use this once you trust what it writes.</small>
          </ChoiceText>
        </Choice>
      </div>
    </Wrap>
  );
}

export default SequenceEditor;
